import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('change', this.#handleCompanyDetailsChange);
    this.addEventListener('input', this.#handleCompanyDetailsInput);
    this.addEventListener('submit', this.#handleCartSubmit);

    const companyDetails = this.querySelector('cart-company-details');
    const companyToggle = companyDetails?.querySelector('[data-company-toggle]');
    if (companyDetails && companyToggle instanceof HTMLInputElement && companyToggle.checked) {
      this.#setCompanyDetailsVisibility(companyDetails, true);
    }

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('change', this.#handleCompanyDetailsChange);
    this.removeEventListener('input', this.#handleCompanyDetailsInput);
    this.removeEventListener('submit', this.#handleCartSubmit);
  }

  /** @type {boolean} */
  #companyDetailsSubmitReady = false;

  #handleCompanyDetailsChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const details = event.target.closest('cart-company-details');
    if (!details) return;

    if (event.target.matches('[data-company-toggle]')) {
      this.#setCompanyDetailsVisibility(details, event.target.checked);
    }

    this.#saveCompanyDetails(details);
  };

  #handleCompanyDetailsInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-company-address]')) return;
    event.target.setCustomValidity(event.target.value.trim() ? '' : 'Įveskite įmonės adresą.');
  };

  #handleCartSubmit = async (event) => {
    if (this.#companyDetailsSubmitReady) {
      this.#companyDetailsSubmitReady = false;
      return;
    }

    const details = this.querySelector('cart-company-details');
    const toggle = details?.querySelector('[data-company-toggle]');
    if (!details || !(toggle instanceof HTMLInputElement) || !toggle.checked) return;

    const fields = Array.from(details.querySelectorAll('[data-company-field]'));
    const addressField = details.querySelector('[data-company-address]');
    if (addressField instanceof HTMLInputElement) {
      addressField.setCustomValidity(addressField.value.trim() ? '' : 'Įveskite įmonės adresą.');
    }
    const firstInvalid = fields.find((field) => field instanceof HTMLInputElement && !field.reportValidity());
    if (firstInvalid) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const saved = await this.#saveCompanyDetails(details);
    if (!saved) return;

    this.#companyDetailsSubmitReady = true;
    event.target.requestSubmit(event.submitter);
  };

  #setCompanyDetailsVisibility(details, visible) {
    const fieldsContainer = details.querySelector('.cart-company-details__fields');
    const toggle = details.querySelector('[data-company-toggle]');
    const fields = details.querySelectorAll('[data-company-field]');

    fieldsContainer?.toggleAttribute('hidden', !visible);
    details.setAttribute('data-invoice-requested', String(visible));
    toggle?.setAttribute('aria-expanded', String(visible));

    fields.forEach((field) => {
      if (!(field instanceof HTMLInputElement)) return;
      field.disabled = !visible;
      const requiredWhenVisible = [
        'attributes[Company name]',
        'attributes[Company code]',
        'attributes[Company address]',
      ].includes(field.name);
      field.required = visible && requiredWhenVisible;
      if (field.matches('[data-company-address]')) {
        field.setCustomValidity(visible && !field.value.trim() ? 'Įveskite įmonės adresą.' : '');
      }
      if (!visible) {
        field.value = '';
        field.setCustomValidity('');
      }
    });
  }

  async #saveCompanyDetails(details) {
    const toggle = details.querySelector('[data-company-toggle]');
    const fields = Array.from(details.querySelectorAll('[data-company-field]'));
    const status = details.querySelector('.cart-company-details__status');
    if (!(toggle instanceof HTMLInputElement)) return false;

    const values = Object.fromEntries(
      fields.map((field) => [field.name, field instanceof HTMLInputElement ? field.value.trim() : ''])
    );
    const attributes = {
      'Invoice requested': toggle.checked ? 'Yes' : '',
      'Company name': toggle.checked ? values['attributes[Company name]'] : '',
      'Company code': toggle.checked ? values['attributes[Company code]'] : '',
      'VAT code': toggle.checked ? values['attributes[VAT code]'] : '',
      'Company address': toggle.checked ? values['attributes[Company address]'] : '',
      'Invoice email': toggle.checked ? values['attributes[Invoice email]'] : '',
    };

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(`Cart attributes update failed: ${response.status}`);
      if (status) status.textContent = '';
      return true;
    } catch (error) {
      console.warn('[cart-drawer] Company details update failed:', error);
      if (status) status.textContent = 'Nepavyko išsaugoti rekvizitų. Bandykite dar kartą.';
      return false;
    }
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
